/**
 * Sprite geometry (spec 005): size from an image, the off-canvas rule, the
 * drag clamp, the rendered box and hinge compensation. Pure functions; the
 * canvas, the properties panel and the store share them.
 *
 * The rendered box: the sprite's `size` box is placed so its hinge point sits
 * at the resolved position, then scaled and rotated about the hinge (the same
 * rule SceneCanvas and SceneRuntime draw with).
 */

import type { Hinge, Orientation, Sprite, SpriteSize } from '../../../Shared/src/@types';
import { resolveSpriteSize } from '../../../Shared/src/utils/spriteSize';
import { getResponsivePosition, getResponsiveTransform } from './responsive';

export {
  resolveSpriteSize, containRect, DEFAULT_SPRITE_SIZE, sizeFromImage, SPRITE_IMAGE_LONG_SIDE,
} from '../../../Shared/src/utils/spriteSize';

/** A drag or a numeric field never leaves the hinge further out than this (percent of the stage). */
export const SPRITE_POSITION_RANGE: readonly [number, number] = [-50, 150];
/** The stage size of each orientation in canvas pixels. */
export const STAGE_SIZES: Readonly<Record<Orientation, SpriteSize>> = {
  portrait: { width: 375, height: 667 },
  landscape: { width: 667, height: 375 },
  desktop: { width: 1280, height: 720 },
};
export const ORIENTATIONS: readonly Orientation[] = ['portrait', 'landscape', 'desktop'];
const DEFAULT_HINGE: Hinge = { x: 0.5, y: 0.5 };

export function stageSize(orientation: Orientation): SpriteSize {
  return STAGE_SIZES[orientation] ?? STAGE_SIZES.portrait;
}

/** Clamp one position axis (percent) to the legal range. */
export function clampSpriteAxis(value: number): number {
  const [min, max] = SPRITE_POSITION_RANGE;
  return Math.min(max, Math.max(min, value));
}

/** Clamp a hinge position (percent of the stage) to −50 %…150 % on each axis. */
export function clampSpritePosition(p: { x: number; y: number }): { x: number; y: number } {
  return { x: clampSpriteAxis(p.x), y: clampSpriteAxis(p.y) };
}

export interface SpritePose {
  /** Hinge position, percent of the stage. */
  x: number;
  y: number;
  scale: number;
  /** Degrees, clockwise. */
  rotation: number;
  hinge: Hinge;
  size: SpriteSize;
}

/** The sprite's base pose in an orientation (no keyframes). */
export function basePose(sprite: Sprite, orientation: Orientation): SpritePose {
  const pos = getResponsivePosition(sprite.layout, orientation);
  const t = getResponsiveTransform(sprite.layout, orientation, sprite.transform);
  return {
    x: pos.x,
    y: pos.y,
    scale: t.scale ?? 1,
    rotation: t.rotation || 0,
    hinge: sprite.hinge ?? DEFAULT_HINGE,
    size: resolveSpriteSize(sprite),
  };
}

/** The four corners of the rendered box in canvas px (top-left, top-right, bottom-right, bottom-left). */
export function poseCorners(pose: SpritePose, stage: SpriteSize): Array<{ x: number; y: number }> {
  const px = (pose.x / 100) * stage.width;
  const py = (pose.y / 100) * stage.height;
  const rad = (pose.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const { width: w, height: h } = pose.size;
  const local = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  return local.map((p) => {
    const lx = (p.x - pose.hinge.x * w) * pose.scale;
    const ly = (p.y - pose.hinge.y * h) * pose.scale;
    return { x: px + lx * cos - ly * sin, y: py + lx * sin + ly * cos };
  });
}

/** The rendered box's corners for a sprite in an orientation (base values). */
export function spriteCorners(sprite: Sprite, orientation: Orientation): Array<{ x: number; y: number }> {
  return poseCorners(basePose(sprite, orientation), stageSize(orientation));
}

/** True when the rendered box lies wholly outside the stage. */
export function isPoseOffCanvas(pose: SpritePose, stage: SpriteSize): boolean {
  const corners = poseCorners(pose, stage);
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  return (
    Math.max(...xs) <= 0 ||
    Math.min(...xs) >= stage.width ||
    Math.max(...ys) <= 0 ||
    Math.min(...ys) >= stage.height
  );
}

/** True when the sprite's rendered box lies wholly outside the stage in this orientation. */
export function isSpriteOffCanvas(sprite: Sprite, orientation: Orientation): boolean {
  return isPoseOffCanvas(basePose(sprite, orientation), stageSize(orientation));
}

/** One implementation with the `editSprite.hinge` operation (spec 021 v13). */
export { compensateHinge } from '../../../Shared/src/utils/spriteHinge';

/** The natural size of an image blob (rejects when it does not decode). */
export function loadImageNaturalSize(blob: Blob): Promise<SpriteSize> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image did not decode'));
    };
    img.src = url;
  });
}
